import { ProfileAvatar } from '@/components/shared/ProfileAvatar';
import { AIStar, AlertCircle, BookOpen, Button, Check, CheckCheck, CircleFadingPlus, Clock, ExternalLink, Globe, Input, Loader2, MessageCircle, Mic, Pen, RefreshCw, Sparkles, User, X } from '@/components/ui';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/Tooltip';
import { useI18n } from '@/context/I18nProvider';
import { cn } from '@/lib/utils';
import { ApiClient } from '@/services/api-client';
import { MessageFeedback, MessageStatus } from '@/services/types/Lead.types';
import { fToNow, fDateTime, fPreciseTime } from '@/utils/format-time';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import Image from 'next/image';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ChatMessageFeedbackButton } from './ChatMessageFeedbackButton';
import { VoiceMessagePlayer } from './VoiceMessagePlayer';
export type MessageType = 'inbound' | 'outbound';
const isRTL = (text: string): boolean => /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/.test(text);

// Formats a Date as a `datetime-local` input value in the browser's local timezone.
const toDatetimeLocalValue = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
type ChatMessageProps = {
  message: string;
  type: MessageType;
  sender: {
    name: string;
    profilePictureURL?: string;
    id: string;
  };
  timestamp: string;
  leadId?: string;
  messageId?: string;
  isDeleted?: boolean;
  isInstagramConversation?: boolean;
  isComment?: boolean;
  isStoryReply?: boolean;
  mediaUrl?: string;
  externalUrl?: string;
  status?: MessageStatus;
  statusError?: string;
  feedback?: MessageFeedback;
  sentByAI?: boolean;
  buttons?: {
    Title: string;
    URL: string;
  }[] | null;
  quickReplies?: {
    Title: string;
    Payload: string;
  }[] | null;
  isAudio?: boolean;
  audioUrl?: string | null;
  isUpcoming?: boolean;
  scheduledMessageId?: string | null;
  pendingApproval?: boolean | null;
  onScheduledMessageEdited?: () => void;
  onResend?: () => void;
  /** Translation into the user's language, shown instead of the original when set. */
  translation?: string;
};
const ChatMessage = ({
  message,
  type,
  sender,
  timestamp,
  leadId,
  messageId,
  isDeleted = false,
  isInstagramConversation = false,
  isComment = false,
  isStoryReply = false,
  mediaUrl,
  externalUrl,
  status,
  statusError,
  feedback,
  sentByAI = true,
  buttons,
  quickReplies,
  isAudio = false,
  audioUrl,
  isUpcoming = false,
  scheduledMessageId,
  pendingApproval = false,
  onScheduledMessageEdited,
  onResend,
  translation
}: ChatMessageProps) => {
  const {
    t
  } = useI18n();
  const [showOriginal, setShowOriginal] = useState(false);
  const shownMessage = translation && !showOriginal ? translation : message;
  const queryClient = useQueryClient();
  const feedbackButtonRef = useRef<HTMLButtonElement>(null); // Add ref for the feedback button
  const feedbackInputRef = useRef<HTMLDivElement>(null);
  const isOutbound = type === 'outbound';
  const feedbackAlreadySent = !!feedback?.FeedbackContent;
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackValue, setFeedbackValue] = useState('');
  const [thumbnailError, setThumbnailError] = useState(false);
  const [instructionSuggestion, setInstructionSuggestion] = useState<{
    text: string;
    agentId: string;
  } | null>(null);
  const [editSuggestion, setEditSuggestion] = useState<{
    existingInstruction: string;
    revisedInstruction: string;
    target: 'general' | 'followUp' | 'response';
    agentId: string;
  } | null>(null);

  // Scheduled message edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(message);
  const [editScheduledAt, setEditScheduledAt] = useState('');
  const [scheduledFeedbackSent, setScheduledFeedbackSent] = useState(false);
  const showThumbnail = (isComment || isStoryReply) && mediaUrl && !thumbnailError;
  const getFailureReason = (statusError_0?: string): ReactNode => {
    const errorLower = statusError_0?.toLowerCase() || '';

    // Internal errors with no useful detail — show generic message
    const isGenericInternalError = (errorLower.includes('internal_message_sending_error') || errorLower.includes('internal message sending error') || errorLower.includes('internal_response_error') || errorLower.includes('internal response error') || errorLower.includes('meta_message_sending_error') || errorLower.includes('meta message sending error') || errorLower.includes('meta_message_delivery_error') || errorLower.includes('meta message delivery error')) && !errorLower.includes('client error') && !errorLower.includes('forbidden') && !errorLower.includes('unauthorized');
    if (isGenericInternalError) {
      return t('conversations.messageFailed');
    }

    // Meta API errors with detail (e.g. "403 Client Error: Forbidden for url: ...")
    if (errorLower.includes('client error') || errorLower.includes('server error')) {
      // Extract the HTTP status and error type, hide the full URL with access token
      const httpMatch = statusError_0?.match(/^(\d{3}\s+\w+\s+Error:\s*\w+)/);
      if (httpMatch) {
        return `Meta API: ${httpMatch[1]}`;
      }
      return t('conversations.metaApiError');
    }
    switch (true) {
      case errorLower.includes('undeliverable'):
        return t('conversations.leadNotOnWhatsapp');
      case errorLower.includes('payment') || errorLower.includes('currency'):
        return <>
            {t('conversations.paymentMethodError')}{' '}
            <a href="https://business.facebook.com/billing_hub/payment_methods" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80">
              {t('conversations.updatePaymentInfo')}
            </a>{' '}
            {t('conversations.toContinueSending')}
          </>;
      case errorLower.includes('insufficient_balance') || errorLower.includes('insufficient balance'):
        return <div className="space-y-1">
            <p>
              {t('conversations.insufficientBalanceError')}{' '}
              <Link href="/settings/workspace/billing?topup=true" className="underline hover:opacity-80">
                {t('conversations.topUpCredits')}
              </Link>{' '}
              {t('conversations.toContinueSending')}
            </p>
            <p className="text-xs opacity-75">{t('conversations.enableAutoTopUpHint')}</p>
          </div>;
      case errorLower.includes('template') && (errorLower.includes('paused') || errorLower.includes('disabled') || errorLower.includes('unavailable')):
        return t('conversations.templateUnavailableError');
      case errorLower.includes('outside_window') || errorLower.includes('outside window') || errorLower.includes('messaging_window_expired') || errorLower.includes('messaging window expired'):
        return t('conversations.messagingWindowExpired');
      case errorLower.includes('instagram') && errorLower.includes('disconnected'):
        return t('conversations.instagramDisconnectedError');
      case errorLower.includes('message_too_long') || errorLower.includes('message too long'):
        return t('conversations.messageTooLongError');
      case errorLower.includes('self_send') || errorLower.includes('self send'):
        return t('conversations.selfSendError');
      case errorLower.includes('throttle') || errorLower.includes('rate_limit'):
        return t('conversations.messageThrottledError');
      case errorLower.includes('duplicate'):
        return t('conversations.duplicateContentError');
      case errorLower.includes('no_agent_found') || errorLower.includes('no agent found'):
        return <div className="space-y-1">
            <p>{t('conversations.noAgentFoundError')}</p>
            <p>
              <Link href="/agents?type=conversational" className="underline hover:opacity-80">
                {t('conversations.noAgentFoundCTA')}
              </Link>
            </p>
          </div>;
      case !!statusError_0:
        return statusError_0;
      default:
        return t('conversations.messageFailed');
    }
  };

  // ------------------- MUTATIONS -----------------------------------

  const mSendFeedback = useMutation({
    mutationFn: (feedback_0: MessageFeedback) => ApiClient.lead.sendAgentMessageFeedback({
      leadId: leadId!,
      messageId: messageId!,
      feedback: feedback_0
    }),
    onSuccess: async response => {
      queryClient.invalidateQueries({
        queryKey: ['lead.getLead', {
          id: leadId
        }]
      });
      toast.success(t('conversations.feedbackSent'));
      const {
        instructionSuggestion: suggestion,
        editSuggestion: edit,
        agentId: respAgentId
      } = response.data;
      if (suggestion && respAgentId) {
        setInstructionSuggestion({
          text: suggestion,
          agentId: respAgentId
        });
      }
      if (edit && respAgentId) {
        setEditSuggestion({
          ...edit,
          agentId: respAgentId
        });
      }
    },
    onError: () => {
      toast.error(t('conversations.feedbackError'));
    }
  });
  const mAppendInstruction = useMutation({
    mutationFn: ({
      agentId,
      instruction,
      target
    }: {
      agentId: string;
      instruction: string;
      target: 'general' | 'followUp' | 'response';
    }) => ApiClient.agents.appendInstruction({
      agentId,
      payload: {
        instruction,
        target
      }
    }),
    onSuccess: () => {
      toast.success(t('feedback.suggestionApplied'));
      setInstructionSuggestion(null);
    },
    onError: () => {
      toast.error(t('feedback.suggestionError'));
    }
  });
  const mEditInstruction = useMutation({
    mutationFn: ({
      agentId: agentId_0,
      existingInstruction,
      revisedInstruction,
      target: target_0
    }: {
      agentId: string;
      existingInstruction: string;
      revisedInstruction: string;
      target: 'general' | 'followUp' | 'response';
    }) => ApiClient.agents.editInstruction({
      agentId: agentId_0,
      payload: {
        existingInstruction,
        revisedInstruction,
        target: target_0
      }
    }),
    onSuccess: () => {
      toast.success(t('feedback.suggestionApplied'));
      setEditSuggestion(null);
    },
    onError: () => {
      toast.error(t('feedback.suggestionError'));
    }
  });
  const handleSendFeedbacks = async (newFeedbackContent: string) => {
    if (feedbackAlreadySent || !leadId || !messageId) return;
    const newFeedback: MessageFeedback = {
      FeedbackContent: newFeedbackContent,
      isSatisfactory: false
    };
    await mSendFeedback.mutateAsync(newFeedback);
  };

  // ---- Scheduled message edit mutation ----
  const mEditScheduledMessage = useMutation({
    mutationFn: ({
      content,
      scheduledAt
    }: {
      content: string;
      scheduledAt?: Date;
    }) => ApiClient.lead.editScheduledMessage({
      leadId: leadId!,
      scheduledMessageId: scheduledMessageId!,
      content,
      scheduledAt
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['lead.getLead', {
          id: leadId
        }]
      });
      toast.success(t('scheduledMessage.editSuccess'));
      setIsEditing(false);
      onScheduledMessageEdited?.();
    },
    onError: () => {
      toast.error(t('scheduledMessage.editError'));
    }
  });

  // ---- Approve/Reject scheduled message mutations ----
  const mApproveMessage = useMutation({
    mutationFn: () => ApiClient.lead.approveScheduledMessage({
      leadId: leadId!,
      scheduledMessageId: scheduledMessageId!
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['lead.getLead', {
          id: leadId
        }]
      });
      toast.success(t('scheduledMessage.approved'));
    },
    onError: () => {
      toast.error(t('scheduledMessage.approveError'));
    }
  });
  const mRejectMessage = useMutation({
    mutationFn: () => ApiClient.lead.rejectScheduledMessage({
      leadId: leadId!,
      scheduledMessageId: scheduledMessageId!
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['lead.getLead', {
          id: leadId
        }]
      });
      toast.success(t('scheduledMessage.rejected'));
    },
    onError: () => {
      toast.error(t('scheduledMessage.rejectError'));
    }
  });
  const mRetryScheduledMessage = useMutation({
    mutationFn: () => ApiClient.lead.retryScheduledMessage({
      leadId: leadId!,
      scheduledMessageId: scheduledMessageId!
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['lead.getLead', {
          id: leadId
        }]
      });
      toast.success(t('conversations.resendQueued'));
    },
    onError: () => {
      toast.error(t('conversations.resendError'));
    }
  });

  // ---- Scheduled message feedback mutation ----
  const mScheduledFeedback = useMutation({
    mutationFn: (feedback_1: MessageFeedback) => ApiClient.lead.sendScheduledMessageFeedback({
      leadId: leadId!,
      scheduledMessageId: scheduledMessageId!,
      feedback: feedback_1
    }),
    onSuccess: response_0 => {
      toast.success(t('conversations.feedbackSent'));
      setScheduledFeedbackSent(true);
      const {
        instructionSuggestion: suggestion_0,
        editSuggestion: edit_0,
        agentId: respAgentId_0
      } = response_0.data;
      if (suggestion_0 && respAgentId_0) {
        setInstructionSuggestion({
          text: suggestion_0,
          agentId: respAgentId_0
        });
      }
      if (edit_0 && respAgentId_0) {
        setEditSuggestion({
          ...edit_0,
          agentId: respAgentId_0
        });
      }
    },
    onError: () => {
      toast.error(t('conversations.feedbackError'));
    }
  });
  const handleSendScheduledFeedback = async (newFeedbackContent_0: string) => {
    if (scheduledFeedbackSent || !leadId || !scheduledMessageId) return;
    await mScheduledFeedback.mutateAsync({
      FeedbackContent: newFeedbackContent_0,
      isSatisfactory: false
    });
  };

  // -------------------- HOOKS -----------------------------------

  useEffect(() => {
    if (!showFeedbackInput) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowFeedbackInput(false);
        // Blur the feedback button to remove focus-visible/hovered style
        feedbackButtonRef.current?.blur();
      }
    };
    const handleClickOutside = (e_0: MouseEvent) => {
      if (feedbackInputRef.current && feedbackInputRef.current.contains(e_0.target as Node) || feedbackButtonRef.current && feedbackButtonRef.current.contains(e_0.target as Node)) {
        return;
      }
      setShowFeedbackInput(false);
      setFeedbackValue('');
      feedbackButtonRef.current?.blur();
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFeedbackInput]);

  // ------------------- RENDER -----------------------------------

  const renderMessageReceipt = () => {
    if (!isOutbound || isDeleted) return null;
    if (isInstagramConversation && status !== MessageStatus.FAILED) return null;
    switch (status) {
      case MessageStatus.SENDING:
      case 'sending':
        return <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Clock className="size-3 text-text-primary animate-pulse" />
              </TooltipTrigger>
              <TooltipContent className="bg-white text-text-primary font-normal text-center px-2 py-1 border-[#F3F4F6] border shadow">
                {t('conversations.sending')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>;
      case MessageStatus.SENT:
      case 'sent':
        return <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Check className="size-3 text-text-primary" />
              </TooltipTrigger>
              <TooltipContent className="bg-white text-text-primary font-normal text-center px-2 py-1 border-[#F3F4F6] border shadow">
                {t('conversations.sent')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>;
      case MessageStatus.DELIVERED:
      case 'delivered':
        return <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <CheckCheck className="size-3 text-text-primary" />
              </TooltipTrigger>
              <TooltipContent className="bg-white text-text-primary font-normal text-center px-2 py-1 border-[#F3F4F6] border shadow">
                {t('conversations.delivered')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>;
      case MessageStatus.READ:
      case 'read':
        return <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <CheckCheck className="size-3 text-blue-500" />
              </TooltipTrigger>
              <TooltipContent className="bg-white text-text-primary font-normal text-center px-2 py-1 border-[#F3F4F6] border shadow">
                {t('conversations.read')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>;
      case MessageStatus.FAILED:
      case 'failed':
        return <>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 cursor-help">
                    <AlertCircle className="size-3 text-red-500" />
                    <span className="text-red-500 font-normal">{t('conversations.failed')}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="bg-white text-text-primary font-normal text-left max-w-[280px] px-2 py-1 border-[#F3F4F6] border shadow">
                  {getFailureReason(statusError)}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {isUpcoming && scheduledMessageId ? <button type="button" onClick={() => mRetryScheduledMessage.mutate()} disabled={mRetryScheduledMessage.isPending} className="flex items-center gap-1 text-primary hover:underline font-medium disabled:opacity-50">
                <RefreshCw className={cn('size-3', mRetryScheduledMessage.isPending && 'animate-spin')} />
                {t('conversations.resend')}
              </button> : onResend && <button type="button" onClick={onResend} className="flex items-center gap-1 text-primary hover:underline font-medium">
                  <RefreshCw className="size-3" />
                  {t('conversations.resend')}
                </button>}
          </>;
      default:
        return null;
    }
  };
  return <div className={cn('max-w-[450px] flex flex-col gap-1', isOutbound ? 'self-end' : 'self-start')}>
      {showThumbnail && (externalUrl ? <a href={externalUrl} target="_blank" rel="noopener noreferrer" className="group relative w-fit self-start overflow-hidden rounded-xl">
            <Image src={mediaUrl} alt={isComment ? t('conversations.commentedOnPost') : t('conversations.repliedToStory')} width={120} height={120} className="h-[120px] w-[120px] object-cover rounded-xl" onError={() => setThumbnailError(true)} unoptimized />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-xl" />
            <div className="absolute top-1.5 right-1.5 bg-black/50 rounded-full p-1">
              <ExternalLink className="size-3 text-white" />
            </div>
          </a> : <div className="relative w-fit self-start overflow-hidden rounded-xl">
            <Image src={mediaUrl} alt={t('conversations.repliedToStory')} width={120} height={120} className="h-[120px] w-[120px] object-cover rounded-xl" onError={() => setThumbnailError(true)} unoptimized />
          </div>)}

      {isAudio && audioUrl ? <VoiceMessagePlayer audioUrl={audioUrl} transcription={shownMessage} isOutbound={isOutbound} /> : isEditing && isUpcoming && scheduledMessageId ? <div className="flex flex-col gap-1.5 w-full self-end">
          <textarea className="w-full min-h-[80px] rounded-2xl px-3.5 py-2 text-[13px] leading-[18px] font-normal border border-primary/50 bg-white text-text-primary resize-y focus:outline-none focus:ring-1 focus:ring-primary" value={editValue} onChange={e_1 => setEditValue(e_1.target.value)} maxLength={2000} autoFocus onKeyDown={e_2 => {
        if (e_2.key === 'Escape') {
          setIsEditing(false);
          setEditValue(message);
        }
      }} />
          <div className="flex items-center gap-1.5 self-end">
            <Clock className="size-3 text-muted-foreground shrink-0" />
            <input type="datetime-local" className="rounded-lg border border-primary/50 bg-white text-text-primary text-xs px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary" value={editScheduledAt} min={toDatetimeLocalValue(new Date())} onChange={e_3 => setEditScheduledAt(e_3.target.value)} />
          </div>
          <div className="flex gap-1.5 justify-end">
            <Button type="button" variant="default" className="h-7 px-3 text-xs" disabled={!editValue.trim() || editValue.trim() === message && editScheduledAt === toDatetimeLocalValue(new Date(timestamp)) || mEditScheduledMessage.isPending} onClick={() => {
          const newDate = new Date(editScheduledAt);
          const unchanged = editScheduledAt === toDatetimeLocalValue(new Date(timestamp));
          mEditScheduledMessage.mutate({
            content: editValue.trim(),
            scheduledAt: unchanged ? undefined : newDate
          });
        }}>
              {mEditScheduledMessage.isPending ? <Loader2 className="size-3 animate-spin" /> : t('common.save')}
            </Button>
            <Button type="button" variant="ghost" className="h-7 px-3 text-xs" onClick={() => {
          setIsEditing(false);
          setEditValue(message);
        }}>
              {t('common.cancel')}
            </Button>
          </div>
        </div> : <div className={cn('flex items-start gap-1', isOutbound ? 'self-end' : 'self-start')}>
          <p dir={isRTL(shownMessage) ? 'rtl' : 'ltr'} className={cn('w-fit flex gap-2 rounded-2xl px-3.5 py-2 text-[13px] leading-[18px] font-normal whitespace-pre-line break-words', isOutbound ? 'self-end bg-primary text-primary-foreground' : '', !isOutbound && !isDeleted ? 'bg-secondary text-text-primary' : '', isDeleted && 'bg-muted text-muted-foreground', isUpcoming && 'border border-dashed border-amber-300')}>
            {isAudio && <Mic className="size-3.5 shrink-0 mt-0.5 opacity-60" />}
            {shownMessage}
          </p>
          {isUpcoming && scheduledMessageId && !isEditing && <TooltipProvider>
              <Tooltip delayDuration={50}>
                <TooltipTrigger asChild>
                  <Button variant="ghost" type="button" className="size-6 p-0 hover:bg-muted rounded-full shrink-0 mt-1" onClick={() => {
              setEditValue(message);
              setEditScheduledAt(toDatetimeLocalValue(new Date(timestamp)));
              setIsEditing(true);
            }}>
                    <Pen className="size-3 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="bg-white text-text-primary font-normal px-2 py-1 border-[#F3F4F6] border shadow">
                  {t('scheduledMessage.edit')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>}
        </div>}

      {translation && !isEditing && !isDeleted ? <button type="button" className={cn('flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground', isOutbound ? 'self-end' : 'self-start')} onClick={() => setShowOriginal(value => !value)}>
          <Globe className="size-3" />
          {showOriginal ? t('conversations.translation.showTranslation') : t('conversations.translation.showOriginal')}
        </button> : null}

      {pendingApproval && isUpcoming && scheduledMessageId && <div className="flex items-center gap-2 self-end">
          <span className="text-xs text-amber-600 font-medium">
            {t('scheduledMessage.pendingApproval')}
          </span>
          <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs border-red-200 text-red-600 hover:bg-red-50" onClick={() => mRejectMessage.mutate()} disabled={mRejectMessage.isPending || mApproveMessage.isPending}>
            <X className="size-3 mr-1" />
            {t('scheduledMessage.reject')}
          </Button>
          <Button size="sm" className="h-7 px-2.5 text-xs bg-green-600 hover:bg-green-700 text-white" onClick={() => mApproveMessage.mutate()} disabled={mApproveMessage.isPending || mRejectMessage.isPending}>
            <Check className="size-3 mr-1" />
            {t('scheduledMessage.approve')}
          </Button>
        </div>}

      {buttons && buttons.length > 0 && <div className={cn('flex flex-col gap-1.5 w-full', isOutbound ? 'items-end' : 'items-start')}>
          {buttons.map((btn, idx) => <a key={idx} href={btn.URL} target="_blank" rel="noopener noreferrer" className={cn('inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium border transition-colors', isOutbound ? 'border-primary/30 text-primary bg-primary/5 hover:bg-primary/10' : 'border-primary/30 text-primary bg-secondary hover:bg-secondary/80')}>
              <ExternalLink className="size-3 shrink-0" />
              {btn.Title}
            </a>)}
        </div>}

      {quickReplies && quickReplies.length > 0 && <div className={cn('flex flex-wrap gap-1.5 w-full', isOutbound ? 'justify-end' : 'justify-start')}>
          {quickReplies.map((qr, idx_0) => <span key={idx_0} className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium border border-primary/30 text-primary">
              {qr.Title}
            </span>)}
        </div>}

      <div className="flex gap-2 px-3 text-xs justify-between">
        {isUpcoming && scheduledMessageId ? <ChatMessageFeedbackButton ref={feedbackButtonRef} type={type} leadId={leadId} messageId={scheduledMessageId} showFeedbackInput={showFeedbackInput} setShowFeedbackInput={setShowFeedbackInput} isLoading={mScheduledFeedback.isPending} sentByAI feedbackAlreadySentOverride={scheduledFeedbackSent} /> : <ChatMessageFeedbackButton ref={feedbackButtonRef} type={type} leadId={leadId} messageId={messageId} feedback={feedback} showFeedbackInput={showFeedbackInput} setShowFeedbackInput={setShowFeedbackInput} isLoading={mSendFeedback.isPending} sentByAI={sentByAI} />}

        <div className="flex gap-2 items-center">
          <div className="flex -ml-2 items-center gap-1">
            {isOutbound && sentByAI ? <>
                <div className="h-0.5 w-0.5 rounded-full bg-muted" />
                <div className="flex items-center gap-1 text-transparent bg-clip-text bg-gradient-to-r from-[#926AFF] from-10% via-[#FF77B0] via-30% to-[#FFB367] to-90%">
                  <AIStar className="size-3" />
                  <span className="font-medium">{t('feedback.byOrsayAI')}</span>
                </div>
              </> : isOutbound && !sentByAI ? <>
                <div className="h-0.5 w-0.5 rounded-full bg-muted" />
                <div className="flex items-center gap-1 text-muted-foreground">
                  <User className="size-3" />
                  <span className="font-medium">{t('feedback.manualReply')}</span>
                </div>
              </> : <>
                <ProfileAvatar profilePictureURL={sender.profilePictureURL} displayName={sender.name} leadID={sender.id} size="sm" />
                <span className="font-medium text-text-primary">{sender.name}</span>
              </>}
          </div>

          {isComment ? <>
              <div className="h-[3px] w-[3px] rounded-full bg-[#D9D9D9]" />
              <div className="flex items-center gap-1">
                <MessageCircle className="size-3 text-[#4B5563]" />
                <span className="text-[#4B5563] font-normal ">
                  {t('conversations.commentedOnPost')}
                </span>
              </div>
            </> : isStoryReply ? <>
              <div className="h-[3px] w-[3px] rounded-full bg-[#D9D9D9]" />
              <div className="flex items-center gap-1">
                <CircleFadingPlus className="size-3 text-[#4B5563]" />
                <span className="text-[#4B5563] font-normal ">
                  {t('conversations.repliedToStory')}
                </span>
              </div>
            </> : null}

          <div className="h-[3px] w-[3px] rounded-full bg-[#D9D9D9]" />

          {isUpcoming ? <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200">
              <Clock className="size-3 text-amber-600" />
              <span className="text-amber-700 font-medium">
                {t('messages.scheduledFor')} {fDateTime(timestamp, 'dd MMM HH:mm:ss')}
              </span>
            </div> : <span className="text-text-primary font-normal tabular-nums" title={`${fDateTime(timestamp, 'dd MMM yyyy HH:mm:ss')} · ${fToNow(timestamp)}`}>
              {fPreciseTime(timestamp)}
            </span>}

          {renderMessageReceipt()}
        </div>
      </div>

      {showFeedbackInput ? <div ref={feedbackInputRef} className="h-7 flex items-center gap-1 pl-3">
          <Input type="text" className="h-7 w-64 bg-white border border-black rounded-lg px-2 py-1 text-xs focus-visible:border-black" maxLength={150} placeholder={t('feedback.placeholder')} value={feedbackValue} onChange={e_4 => setFeedbackValue(e_4.target.value)} onKeyDown={e_5 => {
        if (e_5.key === 'Enter' && feedbackValue.trim()) {
          if (isUpcoming && scheduledMessageId) {
            handleSendScheduledFeedback(feedbackValue.trim());
          } else {
            handleSendFeedbacks(feedbackValue.trim());
          }
          setShowFeedbackInput(false);
          setFeedbackValue('');
        }
      }} />
          <Button type="button" variant="secondary" className="h-7 w-14 py-1" disabled={!feedbackValue.trim()} onClick={() => {
        if (isUpcoming && scheduledMessageId) {
          handleSendScheduledFeedback(feedbackValue.trim());
        } else {
          handleSendFeedbacks(feedbackValue.trim());
        }
        setShowFeedbackInput(false);
        setFeedbackValue('');
      }}>
            {t('feedback.send')}
          </Button>
          <Button type="button" variant="default" className="h-7 w-14 py-1" onClick={() => setShowFeedbackInput(false)} aria-label={t('feedback.close')}>
            {t('feedback.close')}
          </Button>
        </div> : null}

      {(mSendFeedback.isPending || mScheduledFeedback.isPending) && <div className="mt-1 mx-1 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-3 animate-spin" />
          <span>{t('feedback.analyzing')}</span>
        </div>}

      {instructionSuggestion && <div className="mt-1 mx-1 rounded-lg border border-purple-200 bg-purple-50 p-2.5 text-xs">
          <div className="flex items-start gap-2">
            <Sparkles className="size-3.5 text-purple-500 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1 space-y-2">
              <p className="font-medium text-purple-700">{t('feedback.instructionSuggestion')}</p>
              <p className="text-purple-600 italic whitespace-pre-line break-words">
                "{instructionSuggestion.text}"
              </p>
              <p className="text-purple-500">{t('feedback.addToWhich')}</p>
              <div className="flex flex-wrap gap-1.5">
                <Button type="button" variant="outline" className="h-7 px-2.5 text-[11px] gap-1.5 border-purple-300 text-purple-700 hover:bg-purple-100" disabled={mAppendInstruction.isPending} onClick={() => mAppendInstruction.mutate({
              agentId: instructionSuggestion.agentId,
              instruction: instructionSuggestion.text,
              target: 'general'
            })}>
                  <BookOpen className="size-3" />
                  {t('feedback.addToGeneral')}
                </Button>
                <Button type="button" variant="outline" className="h-7 px-2.5 text-[11px] gap-1.5 border-purple-300 text-purple-700 hover:bg-purple-100" disabled={mAppendInstruction.isPending} onClick={() => mAppendInstruction.mutate({
              agentId: instructionSuggestion.agentId,
              instruction: instructionSuggestion.text,
              target: 'followUp'
            })}>
                  <RefreshCw className="size-3" />
                  {t('feedback.addToFollowUp')}
                </Button>
                <Button type="button" variant="outline" className="h-7 px-2.5 text-[11px] gap-1.5 border-purple-300 text-purple-700 hover:bg-purple-100" disabled={mAppendInstruction.isPending} onClick={() => mAppendInstruction.mutate({
              agentId: instructionSuggestion.agentId,
              instruction: instructionSuggestion.text,
              target: 'response'
            })}>
                  <MessageCircle className="size-3" />
                  {t('feedback.addToResponse')}
                </Button>
                <Button type="button" variant="ghost" className="h-7 px-1.5 text-[11px] text-muted-foreground hover:text-foreground" onClick={() => setInstructionSuggestion(null)}>
                  <X className="size-3" />
                </Button>
              </div>
            </div>
          </div>
        </div>}

      {editSuggestion && <div className="mt-1 mx-1 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs">
          <div className="flex items-start gap-2">
            <Pen className="size-3.5 text-amber-600 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1 space-y-2">
              <p className="font-medium text-amber-700">{t('feedback.editSuggestion')}</p>
              <p className="text-amber-600">
                {t('feedback.editTargetLabel')}{' '}
                <span className="font-medium">
                  {t(editSuggestion.target === 'general' ? 'feedback.addToGeneral' : editSuggestion.target === 'followUp' ? 'feedback.addToFollowUp' : 'feedback.addToResponse')}
                </span>
              </p>
              <div className="space-y-1.5">
                <div className="rounded border border-red-200 bg-red-50 p-1.5">
                  <p className="font-medium text-red-500 text-[10px] uppercase tracking-wide">
                    {t('feedback.editBefore')}
                  </p>
                  <p className="text-red-600 whitespace-pre-line break-words line-through decoration-red-300">
                    {editSuggestion.existingInstruction}
                  </p>
                </div>
                <div className="rounded border border-green-200 bg-green-50 p-1.5">
                  <p className="font-medium text-green-600 text-[10px] uppercase tracking-wide">
                    {t('feedback.editAfter')}
                  </p>
                  <p className="text-green-700 whitespace-pre-line break-words">
                    {editSuggestion.revisedInstruction}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Button type="button" variant="outline" className="h-7 px-2.5 text-[11px] gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-100" disabled={mEditInstruction.isPending} onClick={() => mEditInstruction.mutate({
              agentId: editSuggestion.agentId,
              existingInstruction: editSuggestion.existingInstruction,
              revisedInstruction: editSuggestion.revisedInstruction,
              target: editSuggestion.target
            })}>
                  <Check className="size-3" />
                  {t('feedback.applyChange')}
                </Button>
                <Button type="button" variant="ghost" className="h-7 px-1.5 text-[11px] text-muted-foreground hover:text-foreground" onClick={() => setEditSuggestion(null)}>
                  <X className="size-3" />
                </Button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};
export { ChatMessage };